import { Box, Button } from '@factorialco/deprecated-design-system'
import {
  EditProcurementTypeDocument,
  NewRequestPageDocument,
  ProcurementSettingsPageDocument,
  ProcurementTypeIdentityFragment,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface EditRequestTypeForm {
  name: string
  description: string
}

interface Props {
  requestType: ProcurementTypeIdentityFragment
  onClose: () => void
}

const EditRequestTypeModal = ({ requestType, onClose }: Props) => {
  const { company } = useCurrentSession()

  const [editProcurementType] = useMutation(EditProcurementTypeDocument, {
    extractorFn: (data) => data.procurementMutations.updateProcurementType,
  })

  const form = useForm<EditRequestTypeForm>({
    defaultValues: { name: requestType?.name || '', description: requestType?.description || '' },
    validate: () => {
      const errors: FormErrors<EditRequestTypeForm> = {}
      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await editProcurementType({
        variables: {
          id: requestType.id,
          companyId: company.id,
          description: data.description,
          name: data.name,
        },
        refetchQueries: [ProcurementSettingsPageDocument, NewRequestPageDocument],
      })

      const handled = handleResult(result, (errors) => {
        setErrors(errors)
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        handleClose()
      }

      return handled
    },
  })

  const handleSave = async () => {
    form.handleSubmit()
  }

  const handleClose = () => {
    onClose()
  }

  return (
    <Modal
      illustration="input-field"
      title={i18n.t('procurement.purchase_requests.settings.types.modals.detail.title')}
      description={i18n.t('procurement.purchase_requests.settings.types.modals.detail.description')}
      footer={
        <>
          <Button type="secondary" onClick={handleClose}>
            {i18n.t('procurement.purchase_requests.settings.types.modals.detail.buttons.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={form.isSubmitting || !form.dirty}>
            {i18n.t('procurement.purchase_requests.settings.types.modals.detail.buttons.save')}
          </Button>
        </>
      }
      onClose={onClose}
    >
      <Box gap="s16">
        <TextField
          label={i18n.t('procurement.purchase_requests.settings.types.modals.detail.labels.name')}
          onChange={(name) => form.setValues({ ...form.values, name })}
          value={form.values.name}
          error={form.errors.name}
          mandatory
        />
        <TextField
          label={i18n.t(
            'procurement.purchase_requests.settings.types.modals.detail.labels.description'
          )}
          description={i18n.t(
            'procurement.purchase_requests.settings.types.modals.detail.descriptions.length'
          )}
          onChange={(description) => form.setValues({ ...form.values, description })}
          value={form.values.description}
          maxLength={75}
        />
      </Box>
    </Modal>
  )
}

export default EditRequestTypeModal
